'use client';

import {useEffect, useRef, useState} from 'react';

export interface UseStickyOnScrollUpOptions {
  threshold?: number;
  revealOffset?: number;
}

export interface UseStickyOnScrollUpResult {
  visible: boolean;
}

const DEFAULT_THRESHOLD = 8;
const DEFAULT_REVEAL_OFFSET = 80;

export function useStickyOnScrollUp(options: UseStickyOnScrollUpOptions = {}): UseStickyOnScrollUpResult {
  const {threshold = DEFAULT_THRESHOLD, revealOffset = DEFAULT_REVEAL_OFFSET} = options;
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    lastScrollY.current = window.scrollY;
    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const current = window.scrollY;
      const delta = current - lastScrollY.current;
      if (Math.abs(delta) < threshold) {
        return;
      }
      if (current <= revealOffset) {
        setVisible(true);
      } else if (delta > 0) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      lastScrollY.current = current;
    };

    const onScroll = () => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(evaluate);
    };

    window.addEventListener('scroll', onScroll, {passive: true});
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [threshold, revealOffset]);

  return {visible};
}
